import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from './Icons';
import { useT } from '@/lib/i18n';
import { useOnboardingStore } from '@/lib/store';

interface MobileFrameProps {
    children: ReactNode;
    /** Progress 0-100. Hilangkan untuk screen tanpa progress (welcome, success). */
    progress?: number;
    /** Kalau ada, tombol back tampil di header. */
    showBack?: boolean;
    /** Label kecil di header (mis. "Langkah 3 / 7"). */
    stepLabel?: string;
    /** Tampilkan language toggle di pojok kanan atas. */
    showLangToggle?: boolean;
}

/**
 * Frame mobile-first untuk semua screen onboarding.
 * Desktop: tampil sebagai card di tengah dengan shadow ala ponsel.
 * Mobile: full-width.
 */
export default function MobileFrame({
    children,
    progress,
    showBack = false,
    stepLabel,
    showLangToggle = false,
}: MobileFrameProps) {
    const navigate = useNavigate();
    const lang = useOnboardingStore((s) => s.lang);
    const setLang = useOnboardingStore((s) => s.setLang);
    const t = useT();

    const hasHeader = showBack || stepLabel || showLangToggle;

    return (
        <div
            style={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--page-bg)',
            }}
        >
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    maxWidth: 480,
                    margin: '0 auto',
                    background: 'var(--surface)',
                    minHeight: '100vh',
                    position: 'relative',
                    boxShadow: '0 0 40px rgba(15,20,25,0.05)',
                }}
            >
                {/* Progress bar */}
                {typeof progress === 'number' && (
                    <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${progress}%` }} />
                    </div>
                )}

                {/* Header */}
                {hasHeader && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 16px',
                            minHeight: 52,
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {showBack && (
                                <button
                                    onClick={() => navigate(-1)}
                                    aria-label={t.back}
                                    style={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: 8,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'var(--ink-100)',
                                    }}
                                >
                                    <Icon name="back" size={18} color="var(--ink-700)" />
                                </button>
                            )}
                            {stepLabel && (
                                <span
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: 'var(--ink-500)',
                                    }}
                                >
                                    {stepLabel}
                                </span>
                            )}
                        </div>

                        {showLangToggle && (
                            <button
                                onClick={() => setLang(lang === 'id' ? 'en' : 'id')}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    height: 32,
                                    padding: '0 10px',
                                    borderRadius: 8,
                                    background: 'var(--ink-100)',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: 'var(--ink-700)',
                                }}
                            >
                                <Icon name="globe" size={14} />
                                {lang.toUpperCase()}
                            </button>
                        )}
                    </div>
                )}

                {children}
            </div>
        </div>
    );
}
